import React, { useState } from "react";
import styles from "../styles/thoughtProvokingQuestions.module.css";
import DOMPurify from "dompurify";
import MeetAgentModal from "components/Automation/MeetAgentModal";
import { useDispatch, useSelector } from "react-redux";
import {
    triggerFormAutomateThunk,
    triggerMeetAutomateThunk,
} from "slices/chat/thunk";
import { setIsFormSurveyModalOpen, setIsMeetAgentModalOpen } from "../slices/chat/reducer";
import { notifyError, notifySuccess } from "utils/customToasts";
import { useAuth } from "context/AuthContext";
import RunSurveyModal from "components/Automation/RunSurveyModal";
import { generateFromSurveyLinksThunk } from '../slices/chat/thunk';

// Helper function to extract only the first list item
const getFirstListItem = (htmlString) => {
    const div = document.createElement("div");
    div.innerHTML = htmlString;
    const firstLi = div.querySelector("li");
    return firstLi ? `<ol><li>${firstLi.innerHTML.trim()}</li></ol>` : "";
};

// Reusable components
const RenderFormButton = ({ onClick, loading, style = {} }) => (
    <button
        onClick={onClick}
        disabled={loading}
        style={{
            padding: "12px 24px",
            border: "1px solid #7A5AF8",
            background: "transparent",
            color: "#7A5AF8",
            borderRadius: "8px",
            fontWeight: "600",
            cursor: "pointer",
            width: "100%",
            ...style,
        }}
    >
        {loading ? "Preparing Survey..." : "Run a survey"}
    </button>
);

const RenderMeetButton = ({ onClick, style = {} }) => (
    <button
        onClick={onClick}
        style={{
            padding: "12px 24px",
            border: "none",
            background: "#7A5AF8",
            color: "white",
            borderRadius: "8px",
            fontWeight: "600",
            cursor: "pointer",
            width: "100%",
            ...style,
        }}
    >
        Schedule a meeting
    </button>
);

const ThoughtProvokingQuestions = ({ question, automation, automationText = "", lastAskedQuestion = "" }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();
    const userId = user?._id;

    const { isFormSurveyModalOpen, formSurveyLinks } = useSelector((state) => state.ChatReducer);

    if (!question?.trim()) return null;

    const handleFormButtonClick = async () => {
        setLoading(true);
        try {
            await dispatch(generateFromSurveyLinksThunk({ prompt: lastAskedQuestion }));
            setLoading(false);
        } catch (err) {
            notifyError("Something went wrong");
            console.error("Error triggering Form Agent:", err);
            setLoading(false);
        }
    };

    const closeFormModal = () => {
        dispatch(setIsFormSurveyModalOpen(false));
    }

    const handleMeetButtonClick = () => {
        dispatch(setIsMeetAgentModalOpen(true));
        dispatch(
            triggerMeetAutomateThunk({
                prompt: "Initiate meet agent automation",
                user_id: userId,
                members: [],
            })
        );
        notifySuccess("Meet Agent triggered successfully");
    };

    return (
        <div className={styles.thoughtProvokingQuestionsContainer}>
            <h4 style={{ color: "#fff", margin: "8px 0px" }}>
                <b>Recommended Actions</b>
            </h4>

            <div className={styles.thoughtProvokingQuestionBox}>
                {/* Left-aligned question */}
                <div
                    style={{ textAlign: "left" }}
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(getFirstListItem(question)),
                    }}
                />

                {/* Automation messages and buttons below the question */}
                <div style={{ width: "100%", marginTop: "16px", textAlign: "center" }}>
                    {automation?.automate_form && (
                        <>
                            <div
                                style={{ marginBottom: "12px", textAlign: "left" }}
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(automation.automate_form),
                                }}
                            />
                            <RenderFormButton
                                onClick={handleFormButtonClick}
                                loading={loading}
                                style={{ maxWidth: "400px", margin: "0 auto" }}
                            />
                        </>
                    )}

                    {automation?.automate_meet && (
                        <>
                            <div
                                style={{ margin: "20px 0 12px", textAlign: "left" }}
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(automation.automate_meet),
                                }}
                            />
                            <RenderMeetButton
                                onClick={handleMeetButtonClick}
                                style={{ maxWidth: "400px", margin: "0 auto" }}
                            />
                        </>
                    )}
                </div>
            </div>

            <MeetAgentModal userId={userId} />
            <RunSurveyModal
                open={isFormSurveyModalOpen}
                onClose={closeFormModal}
                formSurveyLinks={formSurveyLinks}
            />
        </div>
    );
};

export default ThoughtProvokingQuestions;

